import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNumberFormat } from '../hooks/useLanguageDetection';
import { useGeolocation } from '../hooks/useGeolocation';
import s from './Cta.module.css';

import logoInsta from '../assets/feed/logo-insta.png';
import av1 from '../assets/feed/av-fallback-1.jpg';
import av2 from '../assets/feed/av-fallback-2.jpg';
import av3 from '../assets/feed/av-fallback-3.jpg';
import fotoblur1 from '../assets/chat/fotoblur1.jpg';
import nudesChat1 from '../assets/chat/nudes1-chat1.jpg';
import nudesChat2 from '../assets/chat/nudes1-chat2.jpg';
import packChat from '../assets/chat/pack1.1.chat2.png';
import perfilSemFoto from '../assets/feed/perfil-sem-foto.jpeg';
import story1 from '../assets/chat/chat3-story1.png';
import story2 from '../assets/chat/chat3-story2.png';

const Cta = () => {
  const { t, i18n } = useTranslation();
  const formatNumber = useNumberFormat();
  const { location, loading } = useGeolocation();
  const [countdown, setCountdown] = useState('05:00');
  const [activeFaq, setActiveFaq] = useState(null);
  const [activeTest, setActiveTest] = useState(0);
  const [popup, setPopup] = useState({ show: false, text: '' });
  const [messages, setMessages] = useState([]);
  const chatRef = useRef(null);
  const canvasRef = useRef(null);

  // Debug de localização
  useEffect(() => {
    if (!loading && location) {
      console.log('🌍 [CTA] Localização:', {
        country: location.country_name,
        code: location.country_code,
        city: location.city,
        currentLanguage: i18n.language
      });
    }
  }, [location, loading, i18n]);

  // Dados do perfil salvo no localStorage
  const profile = JSON.parse(localStorage.getItem('current_profile') || '{}');
  const storedUsername = localStorage.getItem('current_username') || 'usuario';
  const avatarUrl = profile.profileImageUrl || perfilSemFoto;
  const followersCount = profile.followersCount || 0;
  const followingCount = profile.followingCount || 0;
  const postCount = profile.postCount || 0;
  const isVerified = profile.is_verified || false;

  // Matrix Canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: false });

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();

    const chars = '01';
    const fontSize = 14;
    let columns = Math.floor(canvas.width / fontSize);
    let drops = new Array(columns).fill(1);

    let animationFrame;
    let lastTime = 0;
    const interval = 1000 / 24;

    const draw = (currentTime) => {
      if (currentTime - lastTime < interval) {
        animationFrame = requestAnimationFrame(draw);
        return;
      }
      lastTime = currentTime;

      ctx.fillStyle = 'rgba(0, 0, 0, 0.08)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#6452D4';
      ctx.font = `${fontSize}px monospace`;

      for (let i = 0; i < drops.length; i++) {
        const text = chars[Math.floor(Math.random() * 2)];
        ctx.fillText(text, i * fontSize, drops[i] * fontSize);
        if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i]++;
      }

      animationFrame = requestAnimationFrame(draw);
    };

    animationFrame = requestAnimationFrame(draw);

    const handleResize = () => {
      resize();
      columns = Math.floor(canvas.width / fontSize);
      drops = new Array(columns).fill(1);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Timer - 5 minutos
  useEffect(() => {
    const TIMER_DURATION = 300000;
    const TIMER_KEY = 'timer_start';

    let startTime = localStorage.getItem(TIMER_KEY);
    if (!startTime) {
      startTime = Date.now().toString();
      localStorage.setItem(TIMER_KEY, startTime);
    }

    const start = parseInt(startTime);

    const updateTimer = () => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, TIMER_DURATION - elapsed);
      const minutes = Math.floor(remaining / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);
      setCountdown(`${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);
      if (remaining === 0) localStorage.removeItem(TIMER_KEY);
    };

    updateTimer();
    const iv = setInterval(updateTimer, 1000);
    return () => clearInterval(iv);
  }, []);

  const chatMsgs = useMemo(() => [
    { text: t('cta.chatAnimation.tryClone'), sent: false },
    { text: t('cta.chatAnimation.buyVip'), sent: true }
  ], [t]);

  // Chat animation
  useEffect(() => {
    let index = 0;
    const timeouts = [];

    const animate = () => {
      if (index >= chatMsgs.length) {
        timeouts.push(setTimeout(() => { setMessages([]); index = 0; animate(); }, 1500));
        return;
      }
      setMessages(prev => [...prev, { ...chatMsgs[index], typing: true, id: Date.now() }]);
      timeouts.push(setTimeout(() => {
        setMessages(prev => [...prev.filter(m => !m.typing), { ...chatMsgs[index], typing: false, id: Date.now() }]);
        timeouts.push(setTimeout(() => { index++; animate(); }, 2000));
      }, 1500));
    };

    timeouts.push(setTimeout(animate, 1000));
    return () => timeouts.forEach(clearTimeout);
  }, [chatMsgs]);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    const iv = setInterval(() => setActiveTest(prev => (prev + 1) % 3), 5000);
    return () => clearInterval(iv);
  }, []);

  const testimonials = [
    { avatar: av1, name: 'Marcosvianad', time: '3h', text: t('cta.testimonials.text0') },
    { avatar: av2, name: 'Gieselferreira_34', time: '5h', text: t('cta.testimonials.text1') },
    { avatar: av3, name: 'o__prozind34', time: '1d', text: t('cta.testimonials.text2') }
  ];

  const faqs = [
    { q: t('cta.faq.q1'), a: t('cta.faq.a1') },
    { q: t('cta.faq.q2'), a: t('cta.faq.a2') },
    { q: t('cta.faq.q3'), a: t('cta.faq.a3') },
    { q: t('cta.faq.q4'), a: t('cta.faq.a4') },
    { q: t('cta.faq.q5'), a: t('cta.faq.a5') }
  ];

  const galleryImages = [nudesChat1, nudesChat2, packChat];

  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleCtaClick = (e) => {
    e.preventDefault();
    if (!sessionStorage.getItem('lead_fired')) {
      if (window.fbq) {
        window.fbq('track', 'Lead');
      }
      sessionStorage.setItem('lead_fired', '1');
    }
    
    // Link diferente baseado na localização
    // IMPORTANTE: Se location não carregou ou não tem country_code, assume Brasil como padrão
    // Isso garante que usuários brasileiros sempre vejam o checkout em reais
    const countryCode = (location?.country_code || 'BR').toUpperCase();
    
    // Lista de países que devem ir para checkout internacional (USD)
    const internationalCountries = ['US', 'GB', 'CA', 'AU', 'NZ', 'IE', 'ZA', 'IN', 'SG', 'PH'];
    const isInternational = internationalCountries.includes(countryCode);
    
    console.log('🔗 Redirecionando:', {
      country_code: countryCode,
      isInternational: isInternational,
      currentLanguage: i18n.language,
      location: location
    });
    
    // Espanhol → Centerpag ES; países anglófonos → Centerpag EN; resto → PerfectPay (BRL)
    let checkoutUrl;
    if (i18n.language === 'es') {
      checkoutUrl = 'https://checkout.centerpag.com/pay/PPU38CQ82SM';
    } else if (isInternational) {
      checkoutUrl = 'https://go.centerpag.com/PPU38CQ7TC8';
    } else {
      checkoutUrl = 'https://go.perfectpay.com.br/PPU38CQ7JHT';
    }
    
    console.log('🔗 URL final:', checkoutUrl);
    window.location.href = checkoutUrl;
  };

  const formatNum = (n) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1).replace('.', ',')} mi`;
    if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1).replace('.', ',')} mil`;
    return formatNumber(n);
  };

  return (
    <div className={s.ctaPage}>
      <canvas ref={canvasRef} className={s.matrixCanvas} />

      {/* Timer Bar */}
      <div className={s.timerBar}>
        <div className={s.timerContent}>
          <div className={s.timerIcon}>⏱️</div>
          <div className={s.timerText}>
            <strong>{t('cta.finalizePurchase')} <span className={s.timerCountdown}>{countdown}</span></strong>
            <span className={s.timerWarning}>
              {t('cta.timerWarning')}{' '}
              <span className={s.usernameRed}>{storedUsername}</span>
            </span>
          </div>
        </div>
      </div>

      <div className={s.container}>
        {/* Logo */}
        <div className={s.logoSection}>
          <img src={logoInsta} alt="Logo" />
          <h1 className={s.mainTitle}>
            {t('cta.biggestTool')}<br />{t('cta.cloneOf')} <span className={s.gradient}>Clone</span>
          </h1>
        </div>

        {/* Profile Card - DADOS DO LOCALSTORAGE */}
        <div className={s.profileCard}>
          <div className={s.profileCardContent}>
            <div className={s.profileAvatarBorder}>
              <div className={s.profileAvatarInner}>
                <img alt="Perfil" src={avatarUrl} />
              </div>
            </div>
            <div className={s.profileInfo}>
              <div className={s.profileUsernameRow}>
                <h2 className={s.profileUsername}>{storedUsername}</h2>
                {isVerified && (
                  <svg className={s.verifiedBadge} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                )}
              </div>
              <p className={s.profileName}>@{storedUsername}</p>
            </div>
          </div>
          <div className={s.profileStats}>
            <div className={s.statItem}>
              <span className={s.statNumber}>{formatNum(postCount)}</span>
              <span className={s.statLabel}>{t('cta.stats.posts')}</span>
            </div>
            <div className={s.statItem}>
              <span className={s.statNumber}>{formatNum(followersCount)}</span>
              <span className={s.statLabel}>{t('cta.stats.followers')}</span>
            </div>
            <div className={s.statItem}>
              <span className={s.statNumber}>{formatNum(followingCount)}</span>
              <span className={s.statLabel}>{t('cta.stats.following')}</span>
            </div>
          </div>
        </div>

        {/* VIP Badge */}
        <div className={s.vipBadge}>
          <div className={s.vipCheckIcon}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className={s.vipBadgeInfo}>
            <strong>{t('cta.vipAccess')} {storedUsername}</strong>
            <span>{t('cta.allInfoAvailable')}</span>
          </div>
        </div>

        <div className={s.scrollIndicator}>
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>

        {/* Features */}
        <section className={s.featuresSection}>
          {/* Mídias */}
          <div className={s.featureCard}>
            <div className={s.featureHeader}>
              <div className={s.featureIcon}>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 className={s.featureTitle}>{t('cta.features.mediasTitle', { username: storedUsername })}</h3>
                <p className={s.featureDesc}>{t('cta.features.mediasDesc')}</p>
              </div>
            </div>
            <div className={s.mediaGrid}>
              <div className={s.mediaLarge} onClick={() => setPopup({ show: true, text: t('cta.popup.medias') })}>
                <img src={fotoblur1} alt="" />
                <div className={s.mediaLock}>
                  <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <rect width="18" height="11" x="3" y="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                </div>
              </div>
              {galleryImages.map((img, i) => (
                <div key={i} className={s.mediaItem} onClick={() => setPopup({ show: true, text: t('cta.popup.medias') })}>
                  <img src={img} alt="" />
                  <div className={s.mediaLock}>
                    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <rect width="18" height="11" x="3" y="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Localização */}
          <div className={s.featureCard}>
            <div className={s.featureHeader}>
              <div className={s.featureIcon}>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <h3 className={s.featureTitle}>{t('cta.features.locationTitle')}</h3>
                <p className={s.featureDesc}>{t('cta.features.locationDesc', { username: storedUsername })}</p>
              </div>
            </div>
            <div className={s.mapContainer}>
              <div className={s.mapWrapper}>
                <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #1a1a2e, #16213e, #0f3460)' }} />
                <div className={s.mapPin}>
                  <img src={avatarUrl} alt="" />
                </div>
              </div>
              <div className={s.mapInfo}>
                <div>
                  <div className={s.mapInfoLabel}>{t('cta.features.locationCurrent')}</div>
                  <div className={s.mapInfoUser}>@{storedUsername}</div>
                </div>
                <button className={s.mapButton} onClick={() => setPopup({ show: true, text: t('cta.popup.location') })}>{t('cta.features.locationView')}</button>
              </div>
            </div>
          </div>

          {/* Stories */}
          <div className={s.featureCard}>
            <div className={s.featureHeader}>
              <div className={s.featureIcon}>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
              <div>
                <h3 className={s.featureTitle}>{t('cta.features.storiesTitle')}</h3>
                <p className={s.featureDesc}>{t('cta.features.storiesDesc', { username: storedUsername })}</p>
              </div>
            </div>
            <div className={s.storiesGrid}>
              {[story1, story2].map((img, i) => (
                <div key={i} className={s.storyItem} onClick={() => setPopup({ show: true, text: t('cta.popup.stories') })}>
                  <img src={img} alt="" />
                  <div className={s.storyOverlay}>
                    <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <rect width="18" height="11" x="3" y="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                    <p>{t('cta.features.storiesLocked')}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Direct */}
          <div className={s.featureCard}>
            <div className={s.featureHeader}>
              <div className={s.featureIcon}>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div>
                <h3 className={s.featureTitle}>{t('cta.features.directTitle')}</h3>
                <p className={s.featureDesc}>{t('cta.features.directDesc', { username: storedUsername })}</p>
              </div>
            </div>
            <div className={s.chatContainer}>
              <div className={s.chatHeader}>
                <div className={s.chatUser}>
                  <div className={s.chatAvatar} style={{ backgroundImage: `url(${avatarUrl})` }} />
                  <div>
                    <div className={s.chatUsername}>{storedUsername}</div>
                    <div className={s.chatStatus}>
                      <span className={s.statusDot} />
                      {t('chat.online')}
                    </div>
                  </div>
                </div>
                <div className={s.chatActions}>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
              <div className={s.chatMessages} ref={chatRef}>
                {messages.map(m => (
                  <div key={m.id} className={`${s.chatMessage} ${m.sent ? s.sent : s.received}`}>
                    {m.typing ? (
                      <div className={s.typingIndicator}><span /><span /><span /></div>
                    ) : m.text}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <div className={s.scrollIndicator}>
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>

        {/* Pricing */}
        <section className={s.pricingSection} id="pricing">
          <div className={s.pricingHeader}>
            <img src={logoInsta} alt="Logo" />
            <h2 className={s.pricingTitle}>
              {t('cta.pricing.headingPre')} <span className={s.gradient}>Revelainsta.ai</span><br />
              {t('cta.pricing.headingMid')}<br />
              <span className={s.highlight}>{storedUsername}</span> {t('cta.pricing.headingPost')}
            </h2>
          </div>
          <div className={s.pricingCard}>
            <div className={s.pricingOld}>{t('cta.pricing.oldPrice')}</div>
            <div className={s.pricingPrice}>
              <span className={s.pricingCurrency}>{t('cta.pricing.currency')}</span>
              <span className={s.pricingValue}>{t('cta.pricing.price')}</span>
              <span className={s.pricingCents}>{t('cta.pricing.cents')}</span>
            </div>
            <div className={s.pricingBadges}>
              <div className={s.badgesTrack}>
                {[...Array(2)].map((_, g) => (
                  <React.Fragment key={g}>
                    <span>
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="14" height="14">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      {t('cta.pricing.oneTimePayment')}
                    </span>
                    <span>
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="14" height="14">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      {t('cta.immediateAccess')}
                    </span>
                    <span>
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="14" height="14">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      {t('cta.pricing.securePayment')}
                    </span>
                    <span>
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="14" height="14">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      {t('cta.pricing.guarantee30Days')}
                    </span>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
          <ul className={s.pricingBenefits}>
            {[
              t('cta.pricing.benefitDirects', { username: storedUsername }),
              t('cta.pricing.benefitMedias'),
              t('cta.features.locationTitle'),
              t('cta.pricing.benefitAlerts', { username: storedUsername }),
              t('cta.pricing.benefitBonus')
            ].map((text, i) => (
              <li key={i}>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <span>{text}</span>
              </li>
            ))}
          </ul>
          <a
            href="https://go.perfectpay.com.br/PPU38CQ7JHT"
            className={s.ctaButton}
            onClick={handleCtaClick}
          >
            <span>{t('cta.accessAll')}</span>
            <span>{t('cta.accessIn2Min')}</span>
          </a>
        </section>

        <div className={s.scrollIndicator}>
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>

        {/* Testimonials */}
        <section className={s.testimonialsSection}>
          <h2 className={s.testimonialsTitle}>
            {t('cta.testimonials.title')} <span className={s.gradient}>Revelainsta.ai</span>
          </h2>
          <div className={s.testimonialsCarousel}>
            <div className={s.testimonialsTrack} style={{ transform: `translateX(-${activeTest * 100}%)` }}>
              {testimonials.map((t, i) => (
                <div key={i} className={s.testimonialSlide}>
                  <div className={s.testimonialCard}>
                    <div className={s.testimonialHeader}>
                      <img src={t.avatar} alt={t.name} />
                      <div>
                        <h4>{t.name}</h4>
                        <span>{t.time}</span>
                      </div>
                    </div>
                    <p className={s.testimonialText}>{t.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className={s.testimonialsDots}>
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  className={`${s.dot} ${activeTest === i ? s.dotActive : ''}`}
                  onClick={() => setActiveTest(i)}
                  aria-label={`${t('cta.testimonials.ariaLabel')} ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Alert */}
        <div className={s.alertBox}>
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span>{t('cta.sensitiveInfo.pre')}<strong>{t('cta.sensitiveInfo.bold')}</strong>{t('cta.sensitiveInfo.post')}</span>
        </div>

        {/* FAQ */}
        <section className={s.faqSection}>
          <h2 className={s.faqTitle}>{t('cta.faq.title')}</h2>
          <div className={s.faqList}>
            {faqs.map((item, i) => (
              <div key={i} className={`${s.faqItem} ${activeFaq === i ? s.faqItemActive : ''}`}>
                <button className={s.faqButton} onClick={() => setActiveFaq(activeFaq === i ? null : i)}>
                  <span>{item.q}</span>
                  <span className={s.faqIcon}>{activeFaq === i ? '−' : '+'}</span>
                </button>
                <div className={`${s.faqAnswer} ${activeFaq === i ? s.faqAnswerOpen : ''}`}>
                  <p>{item.a}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Guarantee */}
        <div className={s.guaranteeBox}>
          <div className={s.guaranteeIcon}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <h3>{t('cta.guarantee.title')}</h3>
          <p>{t('cta.guarantee.description')}</p>
        </div>

        {/* Footer Warning */}
        <div className={s.footerWarning}>
          <div className={s.footerWarningContent}>
            <div className={s.footerWarningText}>
              <strong>{t('cta.finalizePurchase')} {countdown}</strong>
              <span>{t('cta.timerWarning')} {storedUsername}.</span>
            </div>
            <button className={s.footerWarningButton} onClick={scrollToPricing}>
              {t('cta.unlockAccess')}
            </button>
          </div>
        </div>
      </div>

      {/* Popup */}
      {popup.show && (
        <>
          <div className={s.popupOverlay} onClick={() => setPopup({ show: false, text: '' })} />
          <div className={s.popupContent}>
            <div className={s.popupIcon}>⚠️</div>
            <h3>{t('cta.blockedAction')}</h3>
            <p>{t('cta.beVipMember')} {popup.text}</p>
            <button onClick={() => { setPopup({ show: false, text: '' }); scrollToPricing(); }}>
              {t('cta.acquireVipAccess')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Cta;